import { type Ref } from 'vue';
import { type TripSearchForm } from '@search/trip/TripSearchForm';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';
import { type ItineraryPathData, ReserveType } from '@utils/itinerary';
import { TripType } from '@utils/trip';

export function useInit(pathData: Ref<ItineraryPathData>, path: string) {
    const invoiceStore = useInvoiceStore();
    const { clearAllEntries } = invoiceStore;
    const itineraryStore = useItineraryStore();
    const { noResults, reservableList, nonReservableList, loading } =
        storeToRefs(itineraryStore);
    const currentModule = useCurrentItineraryModule();
    const { currentLoadingStatus } = moduleToRefs(currentModule.value);
    const currentSearchForm = useCurrentSearchForm<TripSearchForm>();
    const searchFormStore = useSearchFormStore();
    const { reserveType } = storeToRefs(searchFormStore);
    const formStore = useFormStore();
    const packageSelectStore = usePackageSelectStore();
    const authStore = useAuthStore();
    const priceTrackStore = usePriceTrackStore();
    const route = useRoute();
    const autoReserveStore = useAutoReserveStore();
    const layoutStore = useLayoutStore();
    const { sendSrpEvent } = useAnalyticsStore();
    const gtm = useGtm();
    const flightForm = useFlightForm();
    const initPage = async () => {
        if (!currentModule.value.hasItinerarySets) {
            formStore.itineraryPackages = [];
            autoReserveStore.resetAutoReserve(true);
            itineraryStore.currentSetIndex = 0;
        }
        if (reserveType.value === ReserveType.Hotel) {
            packageSelectStore.clearSelectedPackages();
        }
        const [firstPackage] = packageSelectStore.selectedPackages;
        if (
            firstPackage &&
            (currentSearchForm.value.tripType === TripType.OneWay ||
                (firstPackage.isTripPackage() &&
                    firstPackage.departureDateTime.toISOString() !==
                        currentSearchForm.value.departureDate?.toISOString()))
        ) {
            packageSelectStore.deselectPackage(0);
        }
        const params = await searchFormStore.loadViewPath(path, pathData.value);
        searchFormStore.initSearchForm();
        if (
            pathData.value.static !== 'true' ||
            !itineraryStore.packagesLength
        ) {
            itineraryStore.initItineraryModule(params);
            if (currentLoadingStatus.value !== LoadingStatus.Loading) {
                await itineraryStore.loadAvailable();
            }
        } else {
            currentModule.value.sortBy = currentModule.value.defaultSortBy;
            currentModule.value.isInitialSort = true;
        }

        if (authStore.loggedIn) {
            priceTrackStore.loadPriceTracks();
        }
        formStore.setCounts({
            adultCount: currentSearchForm.value.adultCount,
            childCount: currentSearchForm.value.childCount,
            infantCount: currentSearchForm.value.infantCount,
        });
        if (!searchFormStore.firstReserveType) {
            searchFormStore.firstReserveType = reserveType.value;
        }
        invoiceStore.priceChangeMessageShown = false;
    };
    onMounted(() => {
        clearAllEntries();
    });
    onBeforeUnmount(() => {
        layoutStore.clearSteps();
    });

    watch(
        currentLoadingStatus,
        status => {
            if (pathData.value.static === 'true') {
                // The static hotel search event has a problem,
                // not sending (at least) posthog, due to not being ready at send time.
                // The best way to avoid bugs to other services is to split the event handling
                // to another function, not changing the event logic for non-statics at all.
                return;
            }
            if (status === LoadingStatus.Error) {
                sendSrpEvent('error');
            } else if (status === LoadingStatus.Offline) {
                sendSrpEvent('offline');
            } else if (status === LoadingStatus.Success) {
                //#region init-show-sheperd
                const key = layoutStore.capacityTrackTourStorageKey;
                const onceShowed = localStorage.getItem(key);
                if (!onceShowed) {
                    nextTick(() => {
                        layoutStore.startCapacityTrackTour(20000);
                    });
                }
                //#endregion
                if (noResults.value) {
                    sendSrpEvent('noResults');
                } else if (reservableList.value.length) {
                    if (nonReservableList.value.length) {
                        sendSrpEvent('haveReservableAndNoneReservableList');
                    } else {
                        sendSrpEvent('haveReservableList');
                    }
                } else if (nonReservableList.value.length) {
                    sendSrpEvent('haveNoneReservableList');
                    if (
                        reserveType.value === ReserveType.Flight &&
                        flightForm.isDomestic
                    ) {
                        gtm?.trackEvent({
                            event: 'conversion',
                            value: {
                                send_to: 'AW-11280856669/OX61CLnyycoYEN3skIMq',
                                value: 1.0,
                                currency: 'AED',
                                aw_remarketing_only: true,
                            },
                        });
                    }
                }
            }
        },
        { immediate: true }
    );

    // for the static hotel srp events only:
    // Yet I'm not sure if non-posthog handlers have bug or not.
    // Leave them as before.
    // Maybe one day, we find out that they have the same bug and need to be
    // fixed, like posthog.
    watch(
        currentLoadingStatus,
        status => {
            if (
                pathData.value.static === 'true' &&
                status === LoadingStatus.Success
            ) {
                // send non-posthog events as usual,
                // in case the posthog has a permanent loading issue or being not available at all.
                sendSrpEvent('landing', ['gtm', 'webengage']);
            }
        },
        { immediate: true }
    );

    // for the static hotel srp events only:
    watch(
        currentLoadingStatus,
        status => {
            if (
                pathData.value.static === 'true' &&
                status === LoadingStatus.Success
            ) {
                sendSrpEvent('landing', ['posthog']);
            }
        },
        { immediate: true }
    );

    watch(loading, value => {
        if (!value) {
            const { gift, mac } = route.query;
            if (gift && mac) {
                authStore.giftsSetter({
                    mac: mac.toString(),
                    code: gift.toString(),
                });
            }
            if (currentSearchForm.value.departureDate) {
                autoReserveStore.departureDate = dayConfig
                    .gregory(currentSearchForm.value.departureDate)
                    .format(formatter());
            }
        }
    });

    watch(
        () => route.fullPath,
        (_, oldValue) => {
            if (!currentSearchForm.value.duplicateViewPath(oldValue)) {
                initPage();
            }
        }
    );

    return initPage;
}
